/**
 * FormPage.tsx
 * PROJ-054: Workflow Automation - Public Form Renderer
 *
 * This page renders workflow form triggers as public, embeddable forms.
 * Supports both test mode (/forms/test/:workflowId/:slug) and production (/forms/:workflowId/:slug).
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle, Send, FlaskConical, Upload, X, FileIcon, ImageIcon, FileText, File } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

// Interface for uploaded files state
interface UploadedFile {
  file: File;
  preview?: string;
  id: string;
}

interface FormField {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'email' | 'number' | 'textarea' | 'select' | 'dropdown' | 'checkbox' | 'date' | 'tel' | 'url' | 'file' | 'datetime' | 'phone' | 'radio' | 'hidden' | 'password';
  placeholder?: string;
  required?: boolean;
  options?: { label: string; value: string }[] | string[];
  defaultValue?: string | boolean;
  helpText?: string;
  validation?: {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    pattern?: string;
    // File-specific validations
    maxFileSize?: number; // in MB
    allowedFileTypes?: string[];
    maxFiles?: number;
  };
}

interface FormConfig {
  workflowId: string;
  workflowName: string;
  formTitle: string;
  formDescription: string | null;
  formSlug: string;
  fields: FormField[];
  submitButtonText: string;
  successMessage: string;
  redirectUrl: string | null;
  showBranding: boolean;
  theme: 'light' | 'dark';
  primaryColor: string;
  allowMultipleSubmissions: boolean;
  isTestMode: boolean;
}

type FormStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error';

export default function FormPage() {
  const { workflowId, slug } = useParams<{ workflowId: string; slug: string }>();
  const [searchParams] = useSearchParams();
  const isTestMode = window.location.pathname.includes('/forms/test/') || searchParams.get('mode') === 'test';

  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [formData, setFormData] = useState<Record<string, string | boolean>>({});
  const [fileData, setFileData] = useState<Record<string, UploadedFile[]>>({});
  const [status, setStatus] = useState<FormStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Fetch form configuration
  useEffect(() => {
    const fetchFormConfig = async () => {
      if (!workflowId || !slug) {
        setStatus('error');
        setErrorMessage('URL de formulario invalida');
        return;
      }

      try {
        const basePath = isTestMode ? '/api/v1/workflow-automation/forms/test' : '/api/v1/workflow-automation/forms';
        const response = await fetch(`${basePath}/${workflowId}/${slug}`);
        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.error?.message || 'Error al cargar el formulario');
        }

        const config = result.data as FormConfig;
        setFormConfig(config);

        // Initialize form data with default values
        const initialData: Record<string, string | boolean> = {};
        config.fields.forEach((field) => {
          if (field.defaultValue !== undefined) {
            initialData[field.name] = field.defaultValue;
          } else if (field.type === 'checkbox') {
            initialData[field.name] = false;
          } else {
            initialData[field.name] = '';
          }
        });
        setFormData(initialData);
        setStatus('ready');
      } catch (err) {
        console.error('[FormPage] Error fetching form config:', err);
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Error al cargar el formulario');
      }
    };

    fetchFormConfig();
  }, [workflowId, slug, isTestMode]);

  // Handle field change
  const handleFieldChange = (fieldName: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Handle file selection
  const handleFileSelect = (fieldName: string, files: FileList | null, field: FormField) => {
    if (!files || files.length === 0) return;

    const maxFiles = field.validation?.maxFiles || 10;
    const maxFileSize = (field.validation?.maxFileSize || 25) * 1024 * 1024; // Convert MB to bytes
    const allowedTypes = field.validation?.allowedFileTypes || [];

    const currentFiles = fileData[fieldName] || [];
    const newFiles: UploadedFile[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      // Check max files limit
      if (currentFiles.length + newFiles.length >= maxFiles) {
        errors.push(`Máximo ${maxFiles} archivos permitidos`);
        return;
      }

      // Check file size
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: El archivo excede el tamaño máximo de ${field.validation?.maxFileSize || 25}MB`);
        return;
      }

      // Check file type if restrictions exist
      if (allowedTypes.length > 0) {
        const isAllowed = allowedTypes.some((type) => {
          if (type.startsWith('.')) {
            return file.name.toLowerCase().endsWith(type.toLowerCase());
          }
          if (type.endsWith('/*')) {
            return file.type.startsWith(type.replace('/*', '/'));
          }
          return file.type === type;
        });
        if (!isAllowed) {
          errors.push(`${file.name}: Tipo de archivo no permitido`);
          return;
        }
      }

      // Create preview for images
      let preview: string | undefined;
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }

      newFiles.push({
        file,
        preview,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      });
    });

    if (errors.length > 0) {
      setErrorMessage(errors[0]);
      setTimeout(() => setErrorMessage(''), 4000);
    }

    if (newFiles.length > 0) {
      setFileData((prev) => ({
        ...prev,
        [fieldName]: [...(prev[fieldName] || []), ...newFiles],
      }));
    }
  };

  // Remove a file
  const handleFileRemove = (fieldName: string, fileId: string) => {
    setFileData((prev) => {
      const files = prev[fieldName] || [];
      const updatedFiles = files.filter((f) => f.id !== fileId);
      // Revoke object URL to avoid memory leaks
      const removedFile = files.find((f) => f.id === fileId);
      if (removedFile?.preview) {
        URL.revokeObjectURL(removedFile.preview);
      }
      return { ...prev, [fieldName]: updatedFiles };
    });
  };

  // Get file icon based on type
  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return ImageIcon;
    if (mimeType.includes('pdf')) return FileText;
    return File;
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle form submission with file support
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formConfig) return;

    // Check if multiple submissions are allowed
    if (hasSubmitted && !formConfig.allowMultipleSubmissions) {
      setStatus('error');
      setErrorMessage('Este formulario solo permite una respuesta');
      return;
    }

    // Validate required file fields
    const fileFields = formConfig.fields.filter((f) => f.type === 'file' && f.required);
    for (const field of fileFields) {
      const files = fileData[field.name] || [];
      if (files.length === 0) {
        setStatus('error');
        setErrorMessage(`Por favor, adjunta un archivo para: ${field.label}`);
        return;
      }
    }

    setStatus('submitting');
    setErrorMessage('');
    setUploadProgress(0);

    try {
      const basePath = isTestMode ? '/api/v1/workflow-automation/forms/test' : '/api/v1/workflow-automation/forms';

      // Check if we have files to upload
      const hasFiles = Object.values(fileData).some((files) => files.length > 0);

      let response: Response;

      if (hasFiles) {
        // Use FormData for multipart/form-data submission
        const submitFormData = new FormData();

        // Add all regular form fields
        Object.entries(formData).forEach(([key, value]) => {
          submitFormData.append(key, String(value));
        });

        // Add all files
        Object.entries(fileData).forEach(([fieldName, files]) => {
          files.forEach((uploadedFile) => {
            submitFormData.append(fieldName, uploadedFile.file);
          });
        });

        // Upload with progress tracking using XMLHttpRequest
        response = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${basePath}/${workflowId}/${slug}/submit`);

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100);
              setUploadProgress(progress);
            }
          };

          xhr.onload = () => {
            const responseText = xhr.responseText;
            resolve(new Response(responseText, {
              status: xhr.status,
              statusText: xhr.statusText,
              headers: { 'Content-Type': 'application/json' },
            }));
          };

          xhr.onerror = () => reject(new Error('Error de red al enviar el formulario'));
          xhr.ontimeout = () => reject(new Error('Tiempo de espera agotado'));

          xhr.send(submitFormData);
        });
      } else {
        // Use JSON for simple form submission (no files)
        response = await fetch(`${basePath}/${workflowId}/${slug}/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
      }

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Error al enviar el formulario');
      }

      // Clean up file previews
      Object.values(fileData).flat().forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });

      setHasSubmitted(true);
      setSuccessMessage(result.data?.message || formConfig.successMessage);
      setStatus('success');

      // Handle redirect if configured
      if (result.data?.redirectUrl) {
        setTimeout(() => {
          window.location.href = result.data.redirectUrl;
        }, 2000);
      }
    } catch (err) {
      console.error('[FormPage] Error submitting form:', err);
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Error al enviar el formulario');
    } finally {
      setUploadProgress(0);
    }
  };

  // Render field based on type
  const renderField = (field: FormField) => {
    const value = formData[field.name];

    switch (field.type) {
      case 'textarea':
        return (
          <Textarea
            id={field.name}
            name={field.name}
            placeholder={field.placeholder}
            required={field.required}
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
            className="min-h-[100px]"
          />
        );

      case 'select':
      case 'dropdown':
        const selectOptions = Array.isArray(field.options)
          ? field.options.map((opt) =>
              typeof opt === 'string' ? { label: opt, value: opt } : opt
            )
          : [];
        return (
          <Select
            value={value as string}
            onValueChange={(v) => handleFieldChange(field.name, v)}
            required={field.required}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder={field.placeholder || 'Seleccionar...'} />
            </SelectTrigger>
            <SelectContent>
              {selectOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.name}
              checked={value as boolean}
              onCheckedChange={(checked) => handleFieldChange(field.name, checked === true)}
            />
            <Label htmlFor={field.name} className="text-sm font-normal cursor-pointer">
              {field.label}
            </Label>
          </div>
        );

      case 'number':
        return (
          <Input
            id={field.name}
            name={field.name}
            type="number"
            placeholder={field.placeholder}
            required={field.required}
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            min={field.validation?.min}
            max={field.validation?.max}
            className="h-11"
          />
        );

      case 'date':
        return (
          <Input
            id={field.name}
            name={field.name}
            type="date"
            required={field.required}
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="h-11"
          />
        );

      case 'file':
        const files = fileData[field.name] || [];
        const maxFiles = field.validation?.maxFiles || 10;
        const acceptedTypes = field.validation?.allowedFileTypes?.join(',') || '';

        return (
          <div className="space-y-3">
            {/* Drop zone */}
            <div
              className={`
                relative border-2 border-dashed rounded-lg p-6 text-center
                transition-all duration-200 ease-in-out cursor-pointer
                ${files.length >= maxFiles
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50 bg-gray-50/50'
                }
              `}
              onClick={() => {
                if (files.length < maxFiles) {
                  document.getElementById(`file-input-${field.name}`)?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (files.length < maxFiles) {
                  e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                }
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                if (files.length < maxFiles) {
                  handleFileSelect(field.name, e.dataTransfer.files, field);
                }
              }}
            >
              <input
                id={`file-input-${field.name}`}
                type="file"
                className="hidden"
                multiple={maxFiles > 1}
                accept={acceptedTypes}
                onChange={(e) => handleFileSelect(field.name, e.target.files, field)}
                disabled={files.length >= maxFiles}
              />
              <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
              <p className="text-sm font-medium text-gray-700">
                {files.length >= maxFiles
                  ? 'Límite de archivos alcanzado'
                  : 'Arrastra archivos aquí o haz clic para seleccionar'
                }
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {field.validation?.allowedFileTypes?.length
                  ? `Formatos: ${field.validation.allowedFileTypes.join(', ')}`
                  : 'PDF, Imágenes, Word, Excel (máx. 25MB por archivo)'
                }
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {files.length} de {maxFiles} archivo(s)
              </p>
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div className="space-y-2">
                {files.map((uploadedFile) => {
                  const FileIconComponent = getFileIcon(uploadedFile.file.type);
                  return (
                    <div
                      key={uploadedFile.id}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                    >
                      {/* Preview or icon */}
                      {uploadedFile.preview ? (
                        <img
                          src={uploadedFile.preview}
                          alt={uploadedFile.file.name}
                          className="h-12 w-12 object-cover rounded-md flex-shrink-0"
                        />
                      ) : (
                        <div className="h-12 w-12 flex items-center justify-center bg-gray-100 rounded-md flex-shrink-0">
                          <FileIconComponent className="h-6 w-6 text-gray-500" />
                        </div>
                      )}
                      {/* File info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {uploadedFile.file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(uploadedFile.file.size)}
                        </p>
                      </div>
                      {/* Remove button */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileRemove(field.name, uploadedFile.id);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'radio':
        const radioOptions = Array.isArray(field.options)
          ? field.options.map((opt) =>
              typeof opt === 'string' ? { label: opt, value: opt } : opt
            )
          : [];
        return (
          <div className="space-y-2">
            {radioOptions.map((opt) => (
              <div key={opt.value} className="flex items-center space-x-2">
                <input
                  type="radio"
                  id={`${field.name}-${opt.value}`}
                  name={field.name}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <Label htmlFor={`${field.name}-${opt.value}`} className="text-sm font-normal cursor-pointer">
                  {opt.label}
                </Label>
              </div>
            ))}
          </div>
        );

      case 'hidden':
        return (
          <input
            type="hidden"
            name={field.name}
            value={(field.defaultValue as string) || (value as string)}
          />
        );

      case 'password':
        return (
          <Input
            id={field.name}
            name={field.name}
            type="password"
            placeholder={field.placeholder}
            required={field.required}
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
            className="h-11"
          />
        );

      case 'datetime':
        return (
          <Input
            id={field.name}
            name={field.name}
            type="datetime-local"
            required={field.required}
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className="h-11"
          />
        );

      case 'phone':
        return (
          <Input
            id={field.name}
            name={field.name}
            type="tel"
            placeholder={field.placeholder || '+56 9 XXXX XXXX'}
            required={field.required}
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            pattern={field.validation?.pattern}
            className="h-11"
          />
        );

      default: // text, email, tel, url
        return (
          <Input
            id={field.name}
            name={field.name}
            type={field.type as string}
            placeholder={field.placeholder}
            required={field.required}
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
            pattern={field.validation?.pattern}
            className="h-11"
          />
        );
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="text-center">
          <div className="relative inline-flex">
            <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-50" />
            <Loader2 className="relative h-12 w-12 animate-spin mx-auto text-blue-500" />
          </div>
          <p className="mt-4 text-gray-600 font-medium">Cargando formulario...</p>
        </div>
      </div>
    );
  }

  // Error state (form not found or not available)
  if (status === 'error' && !formConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-rose-50 p-4 sm:p-6">
        <Card className="w-full max-w-md shadow-xl border-0 overflow-hidden">
          <div className="h-2 w-full bg-gradient-to-r from-red-400 to-rose-500" />
          <CardContent className="pt-8 pb-6 px-6 text-center">
            <div className="relative inline-flex mb-4">
              <AlertCircle className="h-16 w-16 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Formulario no disponible</h2>
            <p className="text-gray-600">{errorMessage}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-emerald-50 p-4 sm:p-6">
        <Card className="w-full max-w-md shadow-xl border-0 overflow-hidden">
          {/* Decorative success bar */}
          <div className="h-2 w-full bg-gradient-to-r from-green-400 to-emerald-500" />
          <CardContent className="pt-10 pb-8 px-8 text-center">
            <div className="relative inline-flex">
              <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-50" />
              <CheckCircle2 className="relative h-20 w-20 text-green-500 mx-auto" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-6 mb-3">
              ¡Enviado correctamente!
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed">{successMessage}</p>
            {formConfig?.redirectUrl && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-6">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Redirigiendo...</span>
              </div>
            )}
            {formConfig?.allowMultipleSubmissions && (
              <Button
                variant="outline"
                className="mt-8 h-11 px-6 font-medium"
                onClick={() => {
                  setStatus('ready');
                  // Reset form data
                  const initialData: Record<string, string | boolean> = {};
                  formConfig?.fields.forEach((field) => {
                    if (field.defaultValue !== undefined) {
                      initialData[field.name] = field.defaultValue;
                    } else if (field.type === 'checkbox') {
                      initialData[field.name] = false;
                    } else {
                      initialData[field.name] = '';
                    }
                  });
                  setFormData(initialData);
                  // Also reset file data
                  setFileData({});
                }}
              >
                Enviar otra respuesta
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Form ready state
  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8 ${
        formConfig?.theme === 'dark'
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-slate-50 via-white to-blue-50'
      }`}
    >
      <Card className="w-full max-w-2xl shadow-xl border-0 overflow-hidden">
        {/* Decorative top bar with primary color */}
        <div
          className="h-2 w-full"
          style={{ backgroundColor: formConfig?.primaryColor || '#3b82f6' }}
        />
        <CardHeader className="pb-4 pt-6 px-6 sm:px-8">
          {isTestMode && (
            <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 text-sm font-medium">
              <FlaskConical className="h-5 w-5 flex-shrink-0" />
              <span>Modo de prueba - Los datos no se procesarán realmente</span>
            </div>
          )}
          <CardTitle
            style={{ color: formConfig?.primaryColor }}
            className="text-2xl sm:text-3xl font-bold"
          >
            {formConfig?.formTitle}
          </CardTitle>
          {formConfig?.formDescription && (
            <CardDescription className="text-base sm:text-lg text-gray-600 mt-2 leading-relaxed">
              {formConfig.formDescription}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="px-6 sm:px-8 pb-8">
          {status === 'error' && errorMessage && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {formConfig?.fields.map((field) => (
              <div key={field.id} className={`space-y-2 ${field.type === 'hidden' ? 'hidden' : ''}`}>
                {field.type !== 'checkbox' && field.type !== 'hidden' && (
                  <Label htmlFor={field.name} className="text-sm font-medium text-gray-700">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                )}
                {renderField(field)}
                {field.helpText && field.type !== 'hidden' && (
                  <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                )}
              </div>
            ))}

            {/* Upload progress bar */}
            {status === 'submitting' && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Subiendo archivos...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 text-base font-medium shadow-md hover:shadow-lg transition-all"
              style={{
                backgroundColor: formConfig?.primaryColor,
                borderColor: formConfig?.primaryColor,
              }}
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {uploadProgress > 0 && uploadProgress < 100 ? `Subiendo... ${uploadProgress}%` : 'Enviando...'}
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  {formConfig?.submitButtonText || 'Enviar'}
                </>
              )}
            </Button>
          </form>

          {formConfig?.showBranding && (
            <div className="mt-8 pt-4 border-t border-gray-100 text-center">
              <span className="text-xs text-gray-400 font-medium">
                Potenciado por{' '}
                <span className="text-gray-500">Lineage Platform</span>
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
